/**
 * Shopify-inspired chart theme
 * Provides consistent, professional colors and styling for all charts
 */

// Soft, professional color palette
export const CHART_COLORS = {
    // Primary metrics - refined professional tones (brighter for dark mode compatibility)
    revenue: '#10B981',      // Emerald-500 - Clear green
    expense: '#EF4444',      // Red-500 - Clear red
    profit: '#6366F1',       // Indigo-500 - Clear indigo

    // Supporting colors - consistent & accessible
    primary: '#6366F1',      // Indigo
    secondary: '#10B981',    // Green
    accent: '#F59E0B',       // Amber - Warm gold
    warning: '#FBBF24',      // Amber-400
    danger: '#EF4444',       // Red-500
    success: '#10B981',      // Emerald-500

    // Neutral palette for UI elements - Cool grays (Slate)
    gray: {
        50: '#F9FAFB',
        100: '#F3F4F6',
        200: '#E5E7EB',
        300: '#D1D5DB',
        400: '#9CA3AF',
        500: '#6B7280',
        600: '#4B5563',
        700: '#374151',
        800: '#1F2937',
        900: '#111827',
    },

    // Chart-specific colors (harmonious palette)
    chart: {
        line1: '#6366F1', // Indigo
        line2: '#10B981', // Emerald
        line3: '#F59E0B', // Amber
        line4: '#EC4899', // Pink-500
        line5: '#8B5CF6', // Violet-500
        line6: '#06B6D4', // Cyan-500
    },

    // Category colors for expense breakdown (distinct but cohesive)
    category: {
        advertising: '#6366F1', // Indigo
        marketing: '#8B5CF6',   // Violet
        returns: '#EF4444',     // Red
        discounts: '#9CA3AF',   // Gray
        fees: '#14B8A6',        // Teal
        shipping: '#F59E0B',    // Amber
        other: '#6B7280',       // Dark Gray
    }
};

// Responsive font sizes
export const CHART_FONTS = {
    title: {
        base: '10px',
        md: '11px',
        lg: '11px',
    },
    subtitle: {
        base: '9px',
        md: '10px',
        lg: '10px',
    },
    axis: {
        base: '8px',
        md: '9px',
        lg: '10px',
    },
    tooltip: {
        base: '10px',
        md: '11px',
        lg: '11px',
    },
    legend: {
        base: '9px',
        md: '10px',
        lg: '10px',
    }
};

// Chart styling constants
export const CHART_STYLES = {
    // Grid styling - barely visible horizontal lines
    grid: {
        stroke: 'var(--border-subtle)',
        strokeDasharray: '0',  // Solid lines for cleaner look
        strokeWidth: 0.5,      // Very thin
        opacity: 0.4,
    },

    // Axis styling - minimal
    axis: {
        stroke: 'transparent', // Hide axis lines completely
        tick: {
            fill: 'var(--text-primary)',
            fontSize: 10,
            fontWeight: 400,
            fontFamily: '-apple-system, BlinkMacSystemFont, "San Francisco", "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
        },
        axisLine: false,
        tickLine: false,
        tickMargin: 10,
    },

    // Line chart styling - sleek
    line: {
        strokeWidth: 2.5,
        type: 'monotone',
        dot: {
            r: 0,
            strokeWidth: 0,
        },
        activeDot: {
            r: 6,
            strokeWidth: 0,
            fill: CHART_COLORS.primary, // Solid color dot
        },
    },

    // Bar chart styling - rounded
    bar: {
        radius: [3, 3, 0, 0] as [number, number, number, number],
        maxBarSize: 40,
        background: { fill: 'transparent' }, // Clean background
    },

    // Tooltip styling - premium popover look
    tooltip: {
        contentStyle: {
            backgroundColor: 'var(--bg-elevated)',
            border: '1px solid var(--border-subtle)', // Subtle border
            borderRadius: '8px',
            boxShadow: '0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)', // Soft shadow
            padding: '10px 14px',
            fontSize: '11px',
            color: 'var(--text-primary)',
            lineHeight: '1.5',
        },
        cursor: {
            stroke: 'var(--border-subtle)',
            strokeWidth: 1,
            strokeDasharray: '3 3',
        },
        itemStyle: {
            padding: 0,
            margin: 0,
            color: 'var(--text-primary)',
        },
    },

    // Responsive margins - minimal but safe for text
    margin: {
        default: { top: 10, right: 10, bottom: 10, left: 10 },
        withAxis: { top: 10, right: 20, bottom: 20, left: 0 },
        comfortable: { top: 20, right: 24, bottom: 20, left: 24 },
    },
};

// Utility function to get currency symbol
export const getCurrencySymbol = (currencyCode?: string): string => {
    const symbols: Record<string, string> = {
        'USD': '$',
        'EUR': '€',
        'GBP': '£',
        'JPY': '¥',
        'CAD': 'C$',
        'AUD': 'A$',
        'CHF': 'Fr',
        'CNY': '¥',
        'SEK': 'kr',
        'NZD': 'NZ$',
        'TND': 'DT',
    };
    return currencyCode ? (symbols[currencyCode] || currencyCode) : '$';
};

// Format large numbers (responsive)
export const formatCompactNumber = (num: number, currencySymbol: string = '$'): string => {
    const absNum = Math.abs(num);
    const sign = num < 0 ? '-' : '';
    if (absNum >= 1000000) {
        return `${sign}${currencySymbol}${(absNum / 1000000).toFixed(2)}M`;
    } else if (absNum >= 1000) {
        return `${sign}${currencySymbol}${(absNum / 1000).toFixed(2)}K`;
    }
    return `${sign}${currencySymbol}${absNum.toFixed(2)}`;
};

// Format currency with full precision
export const formatCurrency = (value: number, currencySymbol: string = '$'): string => {
    const sign = value < 0 ? '-' : '';
    return `${sign}${currencySymbol}${Math.abs(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

// Get responsive font size based on container width
export const getResponsiveFontSize = (
    width: number | undefined,
    sizes: { base: string; md: string; lg: string }
): number => {
    if (!width) return parseInt(sizes.base);
    if (width >= 600) return parseInt(sizes.lg);
    if (width >= 400) return parseInt(sizes.md);
    return parseInt(sizes.base);
};
